import React, { useRef, useEffect } from "react";
import { useQuery } from "react-query";
import {
  createChart,
  CrosshairMode,
  CandlestickSeries,
  HistogramSeries,
} from "lightweight-charts";

import { fetchWithAppCheck } from '../api/fetchWithAppCheck';

const API_BASE = "https://getstockdata-pyhsltxciq-uc.a.run.app/getStockData";

// 🔥 Fetch data
const fetchCryptoData = async (symbol) => {
  // const res = await fetch(`${API_BASE}?symbol=${symbol}&limit=2000`);
  const res = await fetchWithAppCheck(`${API_BASE}?symbol=${symbol}&limit=2000`);
  const json = await res.json();
  return json.results || [];
};

// 🔥 SMART CLEAN (fix duplicates WITHOUT losing newer data)
const cleanData = (rawData) => {
  const map = new Map();

  rawData.forEach((d) => {
    if (
      d.time != null &&
      d.open != null &&
      d.high != null &&
      d.low != null &&
      d.close != null
    ) {
      const newItem = {
        time: Number(d.time),
        open: Number(d.open),
        high: Number(d.high),
        low: Number(d.low),
        close: Number(d.close),
        volume: Number(d.volume || 0),
      };

      const existing = map.get(d.time);

      // ✅ keep BEST data (higher volume usually = correct candle)
      if (!existing || newItem.volume >= existing.volume) {
        map.set(d.time, newItem);
      }
    }
  });

  const cleaned = Array.from(map.values()).sort((a, b) => a.time - b.time);

  // 🔍 DEBUG (you can remove later)
  // console.log("RAW:", rawData.length);
  // console.log("CLEAN:", cleaned.length);
  // console.log(
  //   "LAST DATE:",
  //   new Date(cleaned[cleaned.length - 1]?.time * 1000)
  // );

  return cleaned;
};

export default function Cryptocurrency({ selectedSymbol = "X:BTCUSD" }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // ✅ React Query v3
  const { data = [], isLoading } = useQuery(
    ["crypto", selectedSymbol],
    () => fetchCryptoData(selectedSymbol),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const drawChart = (rawData) => {
    if (!chartRef.current || chartRef.current.clientWidth === 0) return;

    // 🧹 remove old chart
    if (chartInstance.current) {
      chartInstance.current.remove();
      chartInstance.current = null;
    }

    const container = chartRef.current;
    const chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight || 500,
      layout: {
        background: { type: "solid", color: "transparent" },
        textColor: "#333",
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: "#f0f0f0" },
        horzLines: { color: "#f0f0f0" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      rightPriceScale: {
        borderColor: "#ccc",
      },
      timeScale: {
        borderColor: "#ccc",
        timeVisible: true,
      },
    });

    chartInstance.current = chart;

    // ✅ correct API (v3/v4)
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#26a69a",
      downColor: "#ef5350",
      borderVisible: false,
      wickUpColor: "#26a69a",
      wickDownColor: "#ef5350",
    });

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "",
    });

    chart.priceScale("").applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    // 🔥 CLEAN DATA
    const cleaned = cleanData(rawData);

    const candleData = cleaned.map((d) => ({
      time: d.time,
      open: d.open,
      high: d.high,
      low: d.low,
      close: d.close,
    }));

    const volumeData = cleaned.map((d) => ({
      time: d.time,
      value: d.volume,
      color:
        d.close >= d.open
          ? "rgba(38,166,154,0.5)"
          : "rgba(239,83,80,0.5)",
    }));

    // 📊 apply data
    candleSeries.setData(candleData);
    volumeSeries.setData(volumeData);

    chart.timeScale().fitContent();
  };

  // 🔄 redraw on data change
  useEffect(() => {
    if (!isLoading && data.length > 0) {
      drawChart(data);
    }
  }, [data, isLoading]);

  // 📏 resize + cleanup
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current && chartRef.current) {
        chartInstance.current.applyOptions({
          width: chartRef.current.clientWidth,
          height: chartRef.current.clientHeight || 500,
        });
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);

      if (chartInstance.current) {
        chartInstance.current.remove();
        chartInstance.current = null;
      }
    };
  }, []);

  return (
    <div
      className="container-fluid crypto-page-container"
      style={{ position: "relative" }}
    >
      <h1>Cryptos</h1>

      {/* <p>
        Use the top bar search to select a cryptocurrency and view historical
        data.
      </p> */}

      {isLoading && <p>Loading chart...</p>}

      <div className="chart-panel">
        <div className="chart-panel__watermark">{selectedSymbol}</div>
        <div ref={chartRef} className="chart-panel__canvas" />
      </div>
    </div>
  );
}